import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useCommandPalette } from '@/lib/stores/commandPaletteStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { notificacoesApi } from '@/lib/api/core';
import { useOffline } from '@/lib/hooks/useOffline';
import { rotuloDaRota, grupoDaRota } from '@/lib/navegacao';
import { SeletorEscala } from './SeletorEscala';
import { PainelNotificacoes } from './PainelNotificacoes';

interface TopBarProps {
  /**
   * Abre a gaveta de navegação. Só tem efeito visível em tela estreita, onde
   * a barra lateral não cabe -- ver `AppLayout`. Opcional para não quebrar os
   * testes que montam a TopBar sozinha.
   */
  onAbrirMenu?: () => void;
}

export function TopBar({ onAbrirMenu }: TopBarProps = {}) {
  const { pathname } = useLocation();
  const { open } = useCommandPalette();
  const token = useAuthStore((s) => s.token) || '';
  const [notifAberto, setNotifAberto] = useState(false);
  // Compartilhado com PainelNotificacoes para que o listener de "clique
  // fora" reconheça o próprio botão-gatilho e não o trate como "fora".
  const notifBotaoRef = useRef<HTMLButtonElement>(null);

  const rotulo = rotuloDaRota(pathname);
  const grupo = grupoDaRota(pathname);

  // No protótipo o chip "Sincronizado" é fixo. Aqui ele conta as requisições em
  // voo, senão seria só um enfeite verde afirmando algo que ninguém verificou.
  const emVoo = useIsFetching();

  const { online, fila, sincronizando, sincronizar } = useOffline();

  const { data: naoLidas } = useQuery({
    queryKey: ['notificacoes', false],
    queryFn: () => notificacoesApi.list(token),
    enabled: !!token,
    staleTime: 60_000,
    select: (ns) => ns.filter((n) => !n.lida).length,
  });

  return (
    <header className="top" role="banner">
      {/* Primeiro elemento da barra, e escondido por CSS onde a barra lateral
          é permanente: em tela larga não há gaveta para abrir. */}
      <button
        type="button"
        className="btn sm abrir-menu"
        aria-label="Abrir navegação"
        onClick={onAbrirMenu}
      >
        ☰
      </button>

      {!!rotulo && (
        <div className="crumb" aria-label="Caminho de navegação">
          <span>{grupo}</span>
          <span style={{ margin: '0 6px', color: 'var(--ink3)' }}>/</span>
          <b>{rotulo}</b>
        </div>
      )}

      {/* Três estados, não dois: sem conexão, com fila pendente, e em dia. O
          chip anterior só sabia dizer "sincronizado", o que seria mentira com
          registros esperando na fila local. */}
      <span
        className={`chip ${!online ? 'crit' : fila.length > 0 ? 'warn' : emVoo ? 'warn' : 'good'}`}
        role="status"
        aria-live="polite"
        onClick={() => (fila.length > 0 && online ? void sincronizar() : undefined)}
        style={{ cursor: fila.length > 0 && online ? 'pointer' : 'default' }}
        title={
          !online
            ? `Sem conexão. ${fila.length} registro(s) guardado(s) no aparelho, a enviar quando a rede voltar.`
            : fila.length > 0
            ? `${fila.length} registro(s) na fila. Clique para enviar agora.`
            : emVoo
            ? `${emVoo} requisição(ões) em andamento`
            : 'Os dados desta tela estão em dia com o servidor'
        }
      >
        <i />
        {!online
          ? `Offline${fila.length > 0 ? ` · ${fila.length}` : ''}`
          : sincronizando
          ? 'Enviando fila…'
          : fila.length > 0
          ? `${fila.length} a enviar`
          : emVoo
          ? 'Sincronizando…'
          : 'Sincronizado'}
      </span>

      {/* Escala do mandato — select com troca imediata, como o protótipo
          (proto:330), não mais um chip de leitura que navega para /config. */}
      <SeletorEscala />

      <button
        className="search"
        onClick={open}
        aria-label="Abrir paleta de comandos (Ctrl+K)"
        id="cmd-palette-trigger"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="6" cy="6" r="4" />
          <path d="M10 10l3 3" strokeLinecap="round" />
        </svg>
        Buscar ou ir para...
        <kbd>Ctrl K</kbd>
      </button>

      <button
        ref={notifBotaoRef}
        className="btn sm"
        onClick={() => setNotifAberto((v) => !v)}
        aria-label={
          naoLidas ? `Notificações · ${naoLidas} não lidas` : 'Notificações · nenhuma não lida'
        }
        aria-expanded={notifAberto}
        id="topbar-notif-btn"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 1a5 5 0 015 5v3.5l1 1.5H2l1-1.5V6a5 5 0 015-5zm-1 12h2a1 1 0 01-2 0z" />
        </svg>
        {!!naoLidas && <span className="cnt">{naoLidas}</span>}
      </button>

      <PainelNotificacoes
        aberto={notifAberto}
        aoFechar={() => setNotifAberto(false)}
        gatilhoRef={notifBotaoRef}
      />
    </header>
  );
}
